import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Paper,
  TextField,
  Autocomplete,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  TableView as TableViewIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import FieldSeasonTable, { FieldSeasonItem } from '../zones/FieldSeasonTable';
import FieldSeasonIrrigationDialog from '../zones/FieldSeasonIrrigationDialog';

interface Option {
  id: string;
  name: string;
}

interface RebuildResult {
  rowsProcessed: number;
  fieldSeasonsCreated: number;
  fieldSeasonsUpdated: number;
}

// One row per field per season, filled when import rows are approved — the
// Supplementary Data format, and a CCA Market Audit Planting Seed row's
// paddock columns. Values are corrected by re-importing (a newer import's
// values win), so there's no edit dialog. A business's own seasons are
// also on its page, under Map / Records → Other Records.
const FieldSeasonManagement: React.FC = () => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [businesses, setBusinesses] = useState<Option[]>([]);
  const [timePeriods, setTimePeriods] = useState<Option[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [timePeriodId, setTimePeriodId] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<FieldSeasonItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [irrigationItem, setIrrigationItem] = useState<FieldSeasonItem | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildResult, setRebuildResult] = useState<RebuildResult | null>(null);

  useEffect(() => {
    if (!auth.isSignedIn || !auth.tokens?.accessToken) return;
    (async () => {
      const [businessRes, tpRes] = await Promise.all([
        fetch('/api/businesses/list', { headers: authHeaders() }),
        fetch('/api/time-periods', { headers: authHeaders() }),
      ]);
      if (businessRes.ok) setBusinesses((await businessRes.json()).map((b: any) => ({ id: b.id, name: b.name })));
      if (tpRes.ok) setTimePeriods((await tpRes.json()).map((t: any) => ({ id: t.id, name: t.name })));
    })();
  }, [auth.isSignedIn, auth.tokens?.accessToken, authHeaders]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page + 1), pageSize: String(pageSize) });
      if (businessId) params.set('businessId', businessId);
      if (timePeriodId) params.set('timePeriodId', timePeriodId);
      if (search.trim()) params.set('search', search.trim());
      const res = await fetch(`/api/field-seasons?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch field seasons');
      const data = await res.json();
      setItems(data.items);
      setTotalCount(data.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [businessId, timePeriodId, search, page, pageSize, authHeaders]);

  useEffect(() => {
    const timer = setTimeout(fetchItems, 300);
    return () => clearTimeout(timer);
  }, [fetchItems]);

  const handleArchive = async (item: FieldSeasonItem) => {
    if (!window.confirm(`Archive the ${item.timePeriodName} season of ${item.fieldName}? Supplementary import rows that filled it go back to unmatched.`)) return;
    try {
      const res = await fetch(`/api/field-seasons/${item.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to archive field season');
      await fetchItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRebuild = async () => {
    if (!window.confirm('Fill field seasons again from every approved import row, oldest upload first? Newer values replace older ones.')) return;
    setRebuilding(true);
    setError(null);
    setRebuildResult(null);
    try {
      const res = await fetch('/api/field-seasons/rebuild-from-imports', { method: 'POST', headers: authHeaders() });
      if (!res.ok) throw new Error('Rebuild failed');
      setRebuildResult(await res.json());
      await fetchItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <Box>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <TableViewIcon />
          Field Seasons
        </Typography>
        <Tooltip title="For rows approved before field seasons existed, or after adding a missing value to one of the lists">
          <span>
            <Button variant="outlined" startIcon={rebuilding ? <CircularProgress size={16} /> : <SyncIcon />} disabled={rebuilding} onClick={handleRebuild}>
              Rebuild from imports
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} mb={2}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={businesses}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={businesses.find((b) => b.id === businessId) || null}
          onChange={(_, value) => { setBusinessId(value?.id || null); setPage(0); }}
          renderInput={(params) => <TextField {...params} label="Business (and its children)" />}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="field-seasons-admin-season-label">Season</InputLabel>
          <Select labelId="field-seasons-admin-season-label" label="Season" value={timePeriodId} onChange={(e) => { setTimePeriodId(e.target.value); setPage(0); }}>
            <MenuItem value=""><em>Any</em></MenuItem>
            {timePeriods.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Search farm, field, variety"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {rebuildResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRebuildResult(null)}>
          Rebuilt from {rebuildResult.rowsProcessed} import row(s): {rebuildResult.fieldSeasonsCreated} field season(s) created, {rebuildResult.fieldSeasonsUpdated} updated.
        </Alert>
      )}

      {loading && items.length === 0 ? (
        <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No field seasons yet. They're filled by approving Supplementary Data import rows, CCA Market Audit Planting Seed rows with paddock columns, and any record row's Irrigated / Dryland.
          </Typography>
        </Paper>
      ) : (
        <FieldSeasonTable
          items={items}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
          showBusiness
          onArchive={handleArchive}
          onEditIrrigation={setIrrigationItem}
        />
      )}

      <FieldSeasonIrrigationDialog
        item={irrigationItem}
        authHeader={authHeaders()}
        onClose={() => setIrrigationItem(null)}
        onSaved={() => { setIrrigationItem(null); fetchItems(); }}
      />
    </Box>
  );
};

export default FieldSeasonManagement;
